import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  TrendingUp,
  Image,
  Video,
  Music,
  Download,
  Calendar,
  CreditCard,
  Users,
  Zap,
  Loader2,
  ExternalLink,
  RefreshCw
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ServiceUsage {
  name: string;
  count: number;
  credits: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

interface ModelUsage {
  model: string;
  service: string;
  uses: number;
  credits: number;
}

interface DailyUsage {
  date: string;
  dateRaw: string;
  generations: number;
  credits: number;
}

interface TopUser {
  user_id: string;
  email: string;
  display_name: string | null;
  generations: number;
  credits: number;
}

const UsageReport = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("7days");
  const [loading, setLoading] = useState(true);
  const [refreshingCache, setRefreshingCache] = useState(false);

  // Custom date range
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  // Chart mode toggle for daily chart
  const [chartMode, setChartMode] = useState<"generations" | "credits">("generations");

  const [overviewStats, setOverviewStats] = useState({
    totalGenerations: 0,
    creditsUsed: 0,
    activeUsers: 0,
  });

  const [serviceUsage, setServiceUsage] = useState<ServiceUsage[]>([]);
  const [modelUsage, setModelUsage] = useState<ModelUsage[]>([]);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage[]>([]);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);

  const getPeriodDays = (periodValue: string): number => {
    switch (periodValue) {
      case "today":
      case "yesterday": return 1;
      case "7days": return 7;
      case "30days": return 30;
      case "90days": return 90;
      default: return 7;
    }
  };

  // Helper to convert UTC date to Bangkok timezone date string (YYYY-MM-DD)
  const toBangkokDateStr = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' }); // en-CA gives YYYY-MM-DD format
  };

  // Get today's date in Bangkok timezone
  const getTodayBangkok = (): string => {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
  };

  // Refresh cache for today and yesterday
  const handleRefreshCache = async () => {
    setRefreshingCache(true);
    try {
      const todayBangkok = getTodayBangkok();
      const yesterdayBangkok = new Date(new Date(todayBangkok).getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      // Call the aggregate function for today and yesterday
      await supabase.rpc('admin_aggregate_usage_stats' as any, { days_old: 0 });

      toast({
        title: "Cache Refreshed",
        description: "Usage stats have been updated.",
      });

      // Re-fetch data
      fetchData();
    } catch (error) {
      console.error("Error refreshing cache:", error);
      toast({
        title: "Error",
        description: "Failed to refresh cache",
        variant: "destructive",
      });
    } finally {
      setRefreshingCache(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const todayBangkok = getTodayBangkok();
      const yesterdayBangkok = new Date(new Date(todayBangkok).getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      let requestedStartDate: string;
      let requestedEndDate: string;
      let days = getPeriodDays(period);

      if (period === "custom") {
        requestedStartDate = startDate;
        requestedEndDate = endDate;
        days = Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1;
      } else if (period === "today") {
        requestedStartDate = todayBangkok;
        requestedEndDate = todayBangkok;
      } else if (period === "yesterday") {
        requestedStartDate = yesterdayBangkok;
        requestedEndDate = yesterdayBangkok;
      } else {
        requestedStartDate = new Date(new Date(todayBangkok).getTime() - (days - 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        requestedEndDate = todayBangkok;
      }

      // Calculate cutoff: data older than 2 days uses cached stats
      const realtimeCutoffDate = new Date(new Date(todayBangkok).getTime() - 2 * 24 * 60 * 60 * 1000);

      // Fetch active users count
      const { count: activeUsersCount } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true });

      // === HYBRID APPROACH ===
      // 1. Fetch cached daily_usage_stats for all requested dates
      // 2. Fetch real-time generation_logs for last 2 days only to overlay

      // Fetch cached stats for all requested dates
      const { data: cachedStats } = await supabase
        .rpc('get_daily_usage_stats' as any, {
          p_start_date: requestedStartDate,
          p_end_date: requestedEndDate
        }) as { data: any[] | null };

      // Fetch real-time logs for last 2 days only
      const { data: recentLogs, error: logsError } = await supabase
        .from("generation_logs")
        .select("*")
        .gte("created_at", realtimeCutoffDate.toISOString())
        .order("created_at", { ascending: false })
        .limit(5000);

      if (logsError) throw logsError;

      // Process recent logs
      const allRecentLogs = recentLogs || [];
      const completedRecentLogs = allRecentLogs.filter(l => l.status === "completed");

      // Build daily map - initialize all requested dates
      const dailyMap = new Map<string, { generations: number; credits: number }>();

      for (let i = 0; i < days; i++) {
        const baseDate = period === "yesterday" ? new Date(yesterdayBangkok) : (period === "custom" ? new Date(endDate) : new Date(todayBangkok));
        const date = new Date(baseDate.getTime() - i * 24 * 60 * 60 * 1000);
        const dateStr = toBangkokDateStr(date);
        dailyMap.set(dateStr, { generations: 0, credits: 0 });
      }

      // Fill in cached stats (for all dates from cache)
      if (cachedStats && (cachedStats as any[]).length > 0) {
        (cachedStats as any[]).forEach((stat: { stat_date: string; total_generations: number; total_credits_used: number }) => {
          const dateStr = stat.stat_date;
          if (dailyMap.has(dateStr)) {
            dailyMap.set(dateStr, {
              generations: stat.total_generations,
              credits: stat.total_credits_used
            });
          }
        });
      }

      // Override real-time data for recent dates (last 2 days)
      const recentDatesSet = new Set([todayBangkok, yesterdayBangkok]);

      // For recent dates, calculate from real-time logs instead of cache
      const recentDateCounters = new Map<string, { generations: number; credits: number }>();
      recentDatesSet.forEach(d => {
        if (dailyMap.has(d)) {
          recentDateCounters.set(d, { generations: 0, credits: 0 });
        }
      });

      allRecentLogs.forEach((log) => {
        const dateStr = toBangkokDateStr(log.created_at);
        if (recentDateCounters.has(dateStr)) {
          recentDateCounters.get(dateStr)!.generations += 1;
        }
      });

      completedRecentLogs.forEach((log) => {
        const dateStr = toBangkokDateStr(log.created_at);
        if (recentDateCounters.has(dateStr)) {
          recentDateCounters.get(dateStr)!.credits += log.credits_used || 0;
        }
      });

      // Override cache with real-time data for recent dates
      recentDateCounters.forEach((counts, dateStr) => {
        if (dailyMap.has(dateStr)) {
          dailyMap.set(dateStr, counts);
        }
      });

      // Calculate totals combining cached + recent
      let totalGenerations = 0;
      let creditsUsed = 0;
      let photoCount = 0, photoCredits = 0;
      let videoCount = 0, videoCredits = 0;
      let musicCount = 0, musicCredits = 0;
      const modelMap = new Map<string, { uses: number; credits: number; service: string }>();
      const userMap = new Map<string, { generations: number; credits: number }>();

      // Add cached stats totals (for non-recent dates)
      if (cachedStats && (cachedStats as any[]).length > 0) {
        (cachedStats as any[]).forEach((stat: {
          stat_date: string;
          total_generations: number;
          total_credits_used: number;
          photo_generations: number;
          photo_credits: number;
          video_generations: number;
          video_credits: number;
          music_generations: number;
          music_credits: number;
          model_usage: Record<string, { uses: number; credits: number; service: string }>;
          top_users: Array<{ user_id: string; generations: number; credits: number }>;
        }) => {
          // Skip recent dates — those will come from real-time logs
          if (recentDatesSet.has(stat.stat_date)) return;

          totalGenerations += stat.total_generations;
          creditsUsed += stat.total_credits_used;
          photoCount += stat.photo_generations;
          photoCredits += stat.photo_credits;
          videoCount += stat.video_generations;
          videoCredits += stat.video_credits;
          musicCount += stat.music_generations;
          musicCredits += stat.music_credits;

          // Merge model usage
          if (stat.model_usage) {
            Object.entries(stat.model_usage).forEach(([model, data]) => {
              const existing = modelMap.get(model);
              if (existing) {
                existing.uses += data.uses;
                existing.credits += data.credits;
              } else {
                modelMap.set(model, { ...data });
              }
            });
          }

          // Merge top users
          if (stat.top_users) {
            stat.top_users.forEach((user) => {
              const existing = userMap.get(user.user_id);
              if (existing) {
                existing.generations += user.generations;
                existing.credits += user.credits;
              } else {
                userMap.set(user.user_id, { generations: user.generations, credits: user.credits });
              }
            });
          }
        });
      }

      // Add recent logs totals (only for recent dates to avoid double counting)
      allRecentLogs.forEach((log) => {
        const dateStr = toBangkokDateStr(log.created_at);
        if (recentDatesSet.has(dateStr)) {
          totalGenerations += 1;

          const userId = log.user_id;
          const existing = userMap.get(userId);
          if (existing) {
            existing.generations += 1;
          } else {
            userMap.set(userId, { generations: 1, credits: 0 });
          }
        }
      });

      completedRecentLogs.forEach((log) => {
        const dateStr = toBangkokDateStr(log.created_at);
        if (recentDatesSet.has(dateStr)) {
          creditsUsed += log.credits_used || 0;

          if (log.service_type === "photo") {
            photoCount += 1;
            photoCredits += log.credits_used || 0;
          } else if (log.service_type === "video") {
            videoCount += 1;
            videoCredits += log.credits_used || 0;
          } else if (log.service_type === "music") {
            musicCount += 1;
            musicCredits += log.credits_used || 0;
          }

          // Model usage
          const model = log.model;
          const existingModel = modelMap.get(model);
          if (existingModel) {
            existingModel.uses += 1;
            existingModel.credits += log.credits_used || 0;
          } else {
            modelMap.set(model, {
              uses: 1,
              credits: log.credits_used || 0,
              service: log.service_type,
            });
          }

          // User credits
          const userId = log.user_id;
          const existingUser = userMap.get(userId);
          if (existingUser) {
            existingUser.credits += log.credits_used || 0;
          }
        }
      });

      setOverviewStats({
        totalGenerations,
        creditsUsed,
        activeUsers: activeUsersCount || 0,
      });

      setServiceUsage([
        {
          name: "Photo Generation",
          count: photoCount,
          credits: photoCredits,
          icon: Image,
          color: "text-blue-500",
        },
        {
          name: "Video Generation",
          count: videoCount,
          credits: videoCredits,
          icon: Video,
          color: "text-purple-500",
        },
        {
          name: "Music Generation",
          count: musicCount,
          credits: musicCredits,
          icon: Music,
          color: "text-pink-500",
        },
      ]);

      const modelUsageData: ModelUsage[] = Array.from(modelMap.entries())
        .map(([model, data]) => ({
          model,
          service: data.service,
          uses: data.uses,
          credits: data.credits,
        }))
        .sort((a, b) => b.credits - a.credits);

      setModelUsage(modelUsageData);

      const dailyData: DailyUsage[] = Array.from(dailyMap.entries())
        .map(([date, data]) => ({
          date: new Date(date).toLocaleDateString("th-TH", { month: "short", day: "numeric" }),
          dateRaw: date,
          generations: data.generations,
          credits: data.credits,
        }))
        .reverse();

      setDailyUsage(dailyData);

      // Get top 10 user IDs
      const topUserIds = Array.from(userMap.entries())
        .sort((a, b) => b[1].generations - a[1].generations)
        .slice(0, 10)
        .map(([userId]) => userId);

      // Fetch user profiles for these IDs
      if (topUserIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, email, display_name")
          .in("user_id", topUserIds);

        const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

        const topUsersData: TopUser[] = topUserIds.map((userId) => {
          const userData = userMap.get(userId)!;
          const profile = profileMap.get(userId);
          return {
            user_id: userId,
            email: profile?.email || "Unknown",
            display_name: profile?.display_name || null,
            generations: userData.generations,
            credits: userData.credits,
          };
        });

        setTopUsers(topUsersData);
      } else {
        setTopUsers([]);
      }
    } catch (error) {
      console.error("Error fetching usage data:", error);
      toast({
        title: "Error",
        description: "Failed to fetch usage data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (period !== "custom") {
      fetchData();
    }
  }, [period]);

  // Fetch when custom dates change
  const handleCustomDateSearch = () => {
    if (period === "custom" && startDate && endDate) {
      fetchData();
    }
  };

  const handleExport = () => {
    const csvData = [
      ["Date", "Generations", "Credits Used"],
      ...dailyUsage.map((d) => [d.dateRaw, d.generations.toString(), d.credits.toString()]),
    ];

    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `usage-report-${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({
      title: "Export Complete",
      description: "Usage report has been downloaded.",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const overviewStatsDisplay = [
    { label: "Total Generations", value: overviewStats.totalGenerations.toLocaleString(), icon: Zap },
    { label: "Credits Used", value: overviewStats.creditsUsed.toLocaleString(), icon: CreditCard },
    { label: "Total Users", value: overviewStats.activeUsers.toLocaleString(), icon: Users },
  ];

  const maxGenerations = Math.max(...dailyUsage.map((d) => d.generations), 1);
  const maxCredits = Math.max(...dailyUsage.map((d) => d.credits), 1);
  const maxValue = chartMode === "generations" ? maxGenerations : maxCredits;

  // Generate nice Y-axis ticks
  const getYTicks = (max: number): number[] => {
    if (max <= 5) return [0, 1, 2, 3, 4, 5];
    const rawStep = max / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const candidates = [1, 2, 2.5, 5, 10];
    let step = magnitude;
    for (const c of candidates) {
      if (c * magnitude >= rawStep) {
        step = c * magnitude;
        break;
      }
    }
    const ticks: number[] = [];
    for (let v = 0; v <= max + step * 0.5; v += step) {
      ticks.push(Math.round(v));
    }
    // Ensure we have at least 2 ticks
    if (ticks.length === 1) ticks.push(step);
    return ticks;
  };

  const yTicks = getYTicks(maxValue);
  const chartMax = yTicks[yTicks.length - 1] || 1;

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Calendar className="h-5 w-5 text-muted-foreground" />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="7days">Last 7 Days</SelectItem>
              <SelectItem value="30days">Last 30 Days</SelectItem>
              <SelectItem value="90days">Last 90 Days</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>

          {period === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
                className="px-3 py-2 text-sm bg-background border border-input rounded-md"
              />
              <span className="text-muted-foreground">-</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={today}
                className="px-3 py-2 text-sm bg-background border border-input rounded-md"
              />
              <Button size="sm" onClick={handleCustomDateSearch}>
                ค้นหา
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={handleRefreshCache}
            disabled={refreshingCache}
          >
            <RefreshCw className={`h-4 w-4 ${refreshingCache ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" className="gap-2" onClick={handleExport}>
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {overviewStatsDisplay.map((stat, index) => (
          <Card key={index} variant="glass">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-2xl font-bold">{stat.value}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <stat.icon className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Service Usage */}
      <Card variant="glass">
        <CardHeader>
          <CardTitle>Usage by Service</CardTitle>
          <CardDescription>Breakdown of generations by content type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {serviceUsage.map((service, index) => (
              <div key={index} className="p-4 rounded-xl bg-muted/30">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg bg-background flex items-center justify-center ${service.color}`}>
                    <service.icon className="h-5 w-5" />
                  </div>
                  <span className="font-medium">{service.name}</span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Generations</span>
                    <span className="font-medium">{service.count.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Credits Used</span>
                    <span className="font-medium text-primary">{service.credits.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top 10 Users */}
      <Card variant="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Top 10 Users by Generations
          </CardTitle>
          <CardDescription>ผู้ใช้ที่สร้างผลงานมากที่สุดในช่วงเวลานี้</CardDescription>
        </CardHeader>
        <CardContent>
          {topUsers.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No user data available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4">#</th>
                    <th className="text-left py-3 px-4">User</th>
                    <th className="text-right py-3 px-4">Generations</th>
                    <th className="text-right py-3 px-4">Credits Used</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {topUsers.map((user, index) => (
                    <tr
                      key={user.user_id}
                      className="hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin?tab=history&userId=${user.user_id}`)}
                    >
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${index === 0 ? "bg-yellow-500/20 text-yellow-500" :
                          index === 1 ? "bg-gray-400/20 text-gray-400" :
                            index === 2 ? "bg-amber-600/20 text-amber-600" :
                              "bg-muted text-muted-foreground"
                          }`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-medium">{user.display_name || user.email.split("@")[0]}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                          <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-medium">{user.generations.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right font-medium text-primary">{user.credits.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Daily Usage - VERTICAL BAR CHART */}
      <Card variant="glass">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle>Daily Usage Trend</CardTitle>
              <CardDescription>Generations and credits over time</CardDescription>
            </div>
            <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
              <button
                onClick={() => setChartMode("generations")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  chartMode === "generations"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Generations
              </button>
              <button
                onClick={() => setChartMode("credits")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  chartMode === "credits"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Credits
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {dailyUsage.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No usage data available</p>
          ) : (
            <div className="w-full">
              {/* Chart container */}
              <div className="relative flex" style={{ minHeight: "320px" }}>
                {/* Y-axis labels */}
                <div className="flex flex-col justify-between pr-2 py-1" style={{ width: "60px", height: "280px" }}>
                  {[...yTicks].reverse().map((tick, index) => (
                    <span
                      key={index}
                      className="text-xs text-muted-foreground text-right tabular-nums leading-none"
                    >
                      {tick.toLocaleString()}
                    </span>
                  ))}
                </div>

                {/* Chart area */}
                <div className="flex-1 relative" style={{ height: "280px" }}>
                  {/* Grid lines */}
                  <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                    {[...yTicks].reverse().map((_, index) => (
                      <div key={index} className="border-t border-border/30 w-full" />
                    ))}
                  </div>

                  {/* Bars container */}
                  <div className="absolute inset-0 flex items-end gap-[2px] px-1">
                    {dailyUsage.map((day, index) => {
                      const value = chartMode === "generations" ? day.generations : day.credits;
                      const barHeight = chartMax > 0 ? (value / chartMax) * 100 : 0;
                      const isToday = day.dateRaw === getTodayBangkok();

                      return (
                        <div
                          key={index}
                          className="flex-1 flex flex-col items-center justify-end relative group cursor-pointer"
                          style={{ minWidth: 0 }}
                        >
                          {/* Tooltip on hover */}
                          <div className="absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                            <div className="bg-popover border border-border shadow-lg rounded-lg px-3 py-2 text-xs whitespace-nowrap">
                              <p className="font-semibold text-foreground">{day.date}</p>
                              <p className="text-muted-foreground">
                                <span className="text-blue-400">{day.generations.toLocaleString()}</span> generations
                              </p>
                              <p className="text-muted-foreground">
                                <span className="text-emerald-400">{day.credits.toLocaleString()}</span> credits
                              </p>
                            </div>
                          </div>

                          {/* Bar */}
                          <div
                            className={`w-full rounded-t-sm transition-all duration-300 ease-out ${
                              chartMode === "generations"
                                ? isToday
                                  ? "bg-blue-400"
                                  : "bg-blue-500/70 group-hover:bg-blue-400"
                                : isToday
                                  ? "bg-emerald-400"
                                  : "bg-emerald-500/70 group-hover:bg-emerald-400"
                            }`}
                            style={{
                              height: `${Math.max(barHeight, value > 0 ? 1 : 0)}%`,
                              maxWidth: "48px",
                              margin: "0 auto",
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* X-axis labels */}
              <div className="flex" style={{ paddingLeft: "60px" }}>
                <div className="flex-1 flex gap-[2px] px-1">
                  {dailyUsage.map((day, index) => {
                    // Show label every N days depending on how many data points
                    const showEvery = dailyUsage.length > 60 ? 14 : dailyUsage.length > 30 ? 7 : dailyUsage.length > 14 ? 3 : 1;
                    const showLabel = index % showEvery === 0 || index === dailyUsage.length - 1;

                    return (
                      <div key={index} className="flex-1 text-center" style={{ minWidth: 0 }}>
                        {showLabel && (
                          <span className="text-[10px] text-muted-foreground leading-none block mt-1.5 truncate">
                            {day.date}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-blue-500/70" />
                  <span>Generations</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-emerald-500/70" />
                  <span>Credits</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Model Usage Table - Moved to bottom */}
      <Card variant="glass">
        <CardHeader>
          <CardTitle>Usage by Model</CardTitle>
          <CardDescription>Detailed breakdown by AI model</CardDescription>
        </CardHeader>
        <CardContent>
          {modelUsage.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No usage data available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4">Model</th>
                    <th className="text-left py-3 px-4">Service</th>
                    <th className="text-right py-3 px-4">Uses</th>
                    <th className="text-right py-3 px-4">Credits</th>
                    <th className="text-right py-3 px-4">% of Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {modelUsage.map((item, index) => {
                    const totalCredits = modelUsage.reduce((sum, m) => sum + m.credits, 0);
                    const percentage = totalCredits > 0 ? ((item.credits / totalCredits) * 100).toFixed(1) : "0";
                    return (
                      <tr key={index} className="hover:bg-muted/30">
                        <td className="py-3 px-4 font-medium">{item.model}</td>
                        <td className="py-3 px-4 text-muted-foreground capitalize">{item.service}</td>
                        <td className="py-3 px-4 text-right">{item.uses.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right font-medium text-primary">{item.credits.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary rounded-full"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-muted-foreground w-12">{percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UsageReport;
